import { DateTime } from 'luxon'
import { comparisonShiftDays } from '@gozoki/api-types/src/network-comparison'
import {
  DEFAULT_PRESETS,
  getRangeForPreset,
  type DateRangePreset,
} from '~/components/ui/date-range-picker'

import type { NetworkDashboardDateRange } from './types'

// Presets: Hier, 7 / 30 derniers jours, … (excludes « Aujourd'hui »).
export const DASHBOARD_DATE_PRESETS = DEFAULT_PRESETS.filter((p) => p.value !== 'today')

export interface PresetDays {
  start: string | null
  end: string
}

// Same rules as `DEFAULT_PRESETS`, on ISO days relative to `today`; `start: null` = « Toujours ».
export function presetDays(preset: DateRangePreset, today: DateTime): PresetDays {
  const day = today.startOf('day')
  const end = day.minus({ days: 1 })
  if (preset === 'today') return { start: day.toISODate()!, end: day.toISODate()! }
  if (preset === 'all') return { start: null, end: end.toISODate()! }
  const starts: Record<Exclude<DateRangePreset, 'today' | 'all'>, DateTime> = {
    yesterday: end,
    '7d': day.minus({ days: 7 }),
    '30d': day.minus({ days: 30 }),
    '3m': day.minus({ months: 3 }),
    '12m': day.minus({ months: 12 }),
    mtd: day.startOf('month'),
    ytd: day.startOf('year'),
  }
  const start = starts[preset] > end ? end : starts[preset]
  return { start: start.toISODate()!, end: end.toISODate()! }
}

export function getDefaultDateRange(): NetworkDashboardDateRange {
  return getRangeForPreset('7d')
}

function rangeToKey(range: { from: Date | null; to: Date }): string {
  const from = range.from ? DateTime.fromJSDate(range.from).toISODate() : 'null'
  return `${from}|${DateTime.fromJSDate(range.to).toISODate()}`
}

/** Préset dont la plage correspond exactement à `range` (sinon null = plage custom). */
export function getPresetForRange(range: NetworkDashboardDateRange): DateRangePreset | null {
  const key = rangeToKey(range)
  const match = DASHBOARD_DATE_PRESETS.find((preset) => rangeToKey(preset.getRange()) === key)
  return match?.value ?? null
}

export function buildAutoCalendarComparisonRange(
  range: NetworkDashboardDateRange,
): NetworkDashboardDateRange {
  if (range.from === null) {
    return { from: range.from, to: range.to }
  }
  const from = DateTime.fromJSDate(range.from)
  const to = DateTime.fromJSDate(range.to)
  const lengthDays = Math.max(1, Math.floor(to.diff(from, 'days').days) + 1)
  const shiftDays = comparisonShiftDays(lengthDays)
  return {
    from: from.minus({ days: shiftDays }).toJSDate(),
    to: to.minus({ days: shiftDays }).toJSDate(),
  }
}

export type { DateRangePreset }
